import * as React from 'react';

import { useClearDiagram } from 'src/containers/ProjectDiagram/hooks/useClearDiagram';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { ModelConceptClass } from 'src/containers/ProjectDiagram/types/data';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isModelConceptClass } from 'src/containers/ProjectDiagram/utils/data';
import { getAllInheritedAttributes } from 'src/containers/ProjectDiagram/utils/getAllInheritedAttributes';
import { removeModelConceptAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/removeModelConceptAttribute';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { removeModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/removeModelConceptClass';
import { projectAttributesByDomainSelector } from 'src/selectors/projectAttributesByDomain';
import { projectClassHierarchySelector } from 'src/selectors/projectClassHierarchy';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useRemoveModelConceptClass = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const clearDiagram = useClearDiagram();
  const dispatch = useReduxDispatch();
  const hierarchyByIri = useReduxSelector(
    projectClassHierarchySelector(projectId)
  );
  const attributesByDomain = useReduxSelector(
    projectAttributesByDomainSelector(projectId)
  );

  return React.useCallback(
    async ({
      diagram: commitDiagram,
      key,
    }: {
      diagram?: ExtendedDiagram;
      key: string;
    }) => {
      const classIri = getModelConceptClassValue(key);
      const attributes = getAllInheritedAttributes({
        classIri,
        hierarchyByIri,
        attributesByDomain,
      });
      const attributesToRemove = Object.values(attributes).filter(
        (attribute) => {
          const domains = Object.values(attribute.domains);

          // Check if attribute is associated only to this class
          return domains.length === 1 && domains[0].value === classIri;
        }
      );
      const diagramToUse = commitDiagram || diagram;
      if (!diagramToUse) {
        return;
      }
      const nodeData = diagramToUse.model.nodeDataArray.find(
        (nodeData): nodeData is ModelConceptClass =>
          isModelConceptClass(nodeData) && nodeData.key === key
      );
      if (!nodeData) {
        return;
      }

      clearDiagram();
      const didCommit = await commitDiagramModel(
        diagramToUse,
        (model) => {
          if (!commitDiagram) {
            addDeletedData([nodeData])(model);
          }
          removeModelConceptClass(nodeData)(model);
          attributesToRemove.forEach((attribute) =>
            removeModelConceptAttribute({ value: attribute.value })(model)
          );
        },
        {
          attributesToRemoveInfo: [{ attributesToRemove, classIri }],
          shouldCommit: !commitDiagram,
        }
      );

      if (didCommit) {
        dispatch(
          uiSlice.actions.removedKeysFromEntityDrawerHistory({
            projectId,
            entityValues: [nodeData.value],
          })
        );
      }
    },
    [
      attributesByDomain,
      clearDiagram,
      diagram,
      dispatch,
      hierarchyByIri,
      projectId,
    ]
  );
};
